/****************************************************************
 * File: news-display.component.tsx
 * Description: 消息展示组件，左边是图片swiper， 邮编是2行卡片
 * Author: zayfen <zhangyunfeng@pudutech.com>
 * Created: 2022/05/20 10:27
 *
 * History:
 *   Date               Programmer ID    Description
 *  -----------------  ---------------  ------------------------
 *  2022/05/20 10:27   zayfen           Init
 *
 * This unpublished material is proprietary to PuduTech.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of PuduTech.
 *****************************************************************/

import { FC, useEffect, useState } from 'react'
import { SwiperSlide } from 'swiper/react'
import PdImage from '@components/pd-image/pd-image'
import PdSwiper from '@components/pd-swiper/pd-swiper'
import clsx from 'clsx'

export interface NewsDisplayProps {
  id: number
  title: string
  images: Array<{ mediaUrl: string; title: string; fontColor: string }>
  topcard: {
    title: string
    text: string
  }
  bottomcard: {
    title: string
    text: string
  }
}

const NewsDisplay: FC<NewsDisplayProps> = ({ id, title, images, topcard, bottomcard }) => {
  const [isMobileDevice, setMobileDevice] = useState<boolean>(false)
  useEffect(() => {
    setMobileDevice(window?.innerWidth < 1024)
  }, [])

  return (
    <article className="w-full h-fit flex flex-wrap">
      <h3
        className={clsx(
          'w-full',
          'text-2xl px-6 font-semibold',
          'laptop:text-5xl laptop:text-center laptop:font-medium',
          'pb-6 laptop:pb-12'
        )}
      >
        {title}
      </h3>

      <section
        className={[
          'w-full h-426px pr-0 relative',
          'laptop:flex-1 laptop:w-1/2 laptop:h-auto laptop:min-h-168 laptop:pr-2',
        ].join(' ')}
      >
        {images.length > 0 && (
          <PdSwiper
            key={images.length}
            slidesPerView={1}
            spaceBetween={0}
            hideNavigation={isMobileDevice || images.length <= 1}
            hidePagination={false}
            loop={images.length > 1}
            autoplay={images.length > 1}
            paginationOptions={{
              el: '.pd-pagination-container-solutionsItem' + id,
            }}
          >
            {images.map((img, index) => (
              <SwiperSlide key={index}>
                <PdImage
                  src={img.mediaUrl}
                  enableMask={true}
                  width={'100%'}
                  height={'100%'}
                  maskStyle={{
                    background: 'linear-gradient(to top, rgba(0,0,0,.2), rgba(0,0,0,0))',
                  }}
                >
                  <h5
                    className={clsx(
                      'text-white text-2xl font-semibold',
                      'absolute left-8 bottom-12',
                      'w-[calc(100%-4rem)]'
                    )}
                    style={{
                      color: img.fontColor,
                    }}
                  >
                    {img.title}
                  </h5>
                </PdImage>
              </SwiperSlide>
            ))}
          </PdSwiper>
        )}
        <div
          className={clsx(
            'justify-start !w-fit h-fit absolute !left-8 !bottom-8 !translate-x-0 z-10',
            'pd-pagination-container-solutionsItem' + id
          )}
        ></div>
      </section>

      <div
        className={[
          'w-full h-fit gap-4 mx-6 mt-6',
          'laptop:w-1/2 laptop:flex-1 laptop:h-auto laptop:gap-4 laptop:mx-0 laptop:mt-0 laptop:pl-2',
          'flex flex-col',
        ].join(' ')}
      >
        <section
          className={[
            'w-full h-fit p-4',
            'laptop:min-h-336px laptop:py-8 laptop:px-6',
            'relative bg-white',
            "before:block before:w-full before:h-1.5 before:absolute before:top-0 before:left-0 before:content-[''] before:bg-[#f76719]",
          ].join(' ')}
        >
          <h5 className={['text-xl laptop:text-2xl font-semibold'].join(' ')}>{topcard.title}</h5>
          <div
            className={clsx(
              'whitespace-pre-line mt-6 text-sm font-medium !leading-normal',
              'text-black/60',
              'laptop:text-black/90'
            )}
          >
            {topcard?.text}
          </div>
        </section>

        <section
          className={[
            'w-full h-fit p-4',
            'laptop:py-8 laptop:px-6 laptop:flex-1',
            'relative bg-white',
            "before:block before:w-full before:h-1.5 before:absolute before:top-0 before:left-0 before:content-[''] before:bg-pdblue",
          ].join(' ')}
        >
          <h5 className={['text-xl laptop:text-2xl font-semibold'].join(' ')}>{bottomcard.title}</h5>
          <div
            className={clsx(
              'whitespace-pre-line mt-6 text-sm font-medium !leading-normal',
              'text-black/60',
              'laptop:text-black/90'
            )}
          >
            {bottomcard.text}
          </div>
        </section>
      </div>
    </article>
  )
}

export default NewsDisplay
